import {
  animate,
  HTMLMotionProps,
  motion,
  useMotionValue,
  useMotionValueEvent,
  type Variants,
} from 'motion/react';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { Button } from '@/app/components/button';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { ContainerStyled } from '@/app/components/layouts/container';
import { MapControlsMobile } from '@/app/components/shared/dealer/components/shared/map-controls/mobile';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const TOP_SPACE_PX = 142;
const BOTTOM_SPACE_PX = 64;
const SNAP_Y_PERCENT = [0, 0.6, 0.8];
const FOOTER_ANIMATE_VARIANTS: { [key: string]: Variants['slideIn'] } = {
  slideIn: { y: '0%' },
  slideOut: { y: '100%' },
};
const DRAG_TRANSITION = {
  power: 0.2,
  timeConstant: 50,
  bounceStiffness: 200,
  bounceDamping: 30,
};

interface MobileDrawerProps {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  isShowBackButton?: boolean;
  dealerDetail?: boolean;
  onClickBackButton?: () => void;
  onClickCurrentLocation: () => void;
}

export const MobileDrawer = ({
  children,
  header,
  footer,
  isShowBackButton = false,
  dealerDetail = false,
  onClickBackButton,
  onClickCurrentLocation,
}: MobileDrawerProps) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const drawerContentScrollRef = useRef<HTMLDivElement | null>(null);

  const [windowWidth, setWindowWidth] = useState<number>(0);

  const [showFooter, setShowFooter] = useState<boolean>(dealerDetail);
  const [isFullyExpanded, setIsFullyExpanded] = useState<boolean>(false);

  const yPixelOffset = useMotionValue(0);
  const contentHeight = useMotionValue(0);
  const contentHeightSpace = dealerDetail
    ? TOP_SPACE_PX + (!!footer ? BOTTOM_SPACE_PX : 0)
    : BOTTOM_SPACE_PX;

  const getOffsetHeight = (drawerEl: HTMLDivElement) =>
    drawerEl.offsetHeight === 0
      ? window.innerHeight * SNAP_Y_PERCENT[1]
      : drawerEl.offsetHeight;

  const setDrawerHeight = () => {
    const drawerEl = drawerRef.current;
    if (!drawerEl) return;

    const ratio = SNAP_Y_PERCENT[1];
    const offsetHeight = drawerEl.offsetHeight;
    const windowHeightPartial = window.innerHeight * ratio;
    const height =
      offsetHeight < windowHeightPartial ? window.innerHeight : offsetHeight;
    const offset = ratio * height;

    yPixelOffset.set(ratio * height);
    contentHeight.set(height - offset - contentHeightSpace);
  };

  const resizeHandler = () => {
    setWindowWidth(window.innerWidth);
  };

  const getSnapPointsInPixels = () => {
    const drawerEl = drawerRef.current;
    if (!drawerEl) return [];

    const offsetHeight = getOffsetHeight(drawerEl);
    return SNAP_Y_PERCENT.map((val) => val * offsetHeight);
  };

  const findClosestSnapPoint = (targetY: number) => {
    const snapPoints = getSnapPointsInPixels();
    if (snapPoints.length === 0) return targetY;

    let closestPoint = snapPoints[0];
    let minDistance = Math.abs(targetY - snapPoints[0]);

    for (let i = 1; i < snapPoints.length; i++) {
      const distance = Math.abs(targetY - snapPoints[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = snapPoints[i];
      }
    }

    return closestPoint;
  };

  const dragProps: HTMLMotionProps<'div'> = {
    drag: 'y' as const,
    style: { y: yPixelOffset },
    dragConstraints: isShowBackButton ? { top: 16 } : { top: 128 },
    dragTransition: {
      ...DRAG_TRANSITION,
      modifyTarget: (target: number) => findClosestSnapPoint(target),
    },
  };

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  // Handle drawer content size when resizing from mobile <-> tablet
  useEffect(() => {
    setDrawerHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  /**
   * This hook is to handle drag transition and the events conflict between parent drag event and scroll element of drawer content.
   */
  useEffect(() => {
    // Define a threshold in pixels for a downward drag to be considered a collapse trigger.
    const SCROLL_THRESHOLD = 50;
    // Get the current DOM element for the main drawer content area.
    const contentEl = drawerContentRef.current;
    // Stores the initial Y coordinate of the touch when a drag might start.
    let startDragYPoint: number | null = null;
    // Flag to indicate if touchmove events should be processed by this handler (i.e., touch started within drawer content).
    let enableTouchMove = false;
    // Flag to prevent re-triggering the animation if one is already in progress.
    let isAnimateDrawer = false;

    const removeListeners = () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };

    /**
     * Handles the 'touchstart' event.
     * Determines if the touch initiated within the drawer content area to enable subsequent touchmove tracking.
     */
    const onTouchStart = (e) => {
      const targetEl = e?.target;
      // If no target or content element, or if a drawer animation is already active, ignore.
      if (!targetEl || !contentEl || isAnimateDrawer) return;

      window.addEventListener('touchmove', onTouchMove, { passive: true });

      // Enable touchmove tracking only if the touch started directly on the contentEl
      // or on a descendant of contentEl.
      enableTouchMove = targetEl === contentEl || contentEl.contains(targetEl);
    };

    /**
     * Handles the 'touchmove' event.
     * This is the core logic for detecting a "pull-down-to-collapse" gesture.
     */
    const onTouchMove = (e) => {
      // If content element is not available, touchmove is not enabled, or an animation is active, abort.
      if (!contentEl || !enableTouchMove || isAnimateDrawer) return;

      const targetEl = e?.target;
      const contentScrollEl = drawerContentScrollRef.current;
      let isScrolledAtTop = true; // Assume at top by default

      // Check if the current touch is within the designated scrollable area
      // and if that area is actually scrolled to the very top.
      if (
        !!targetEl &&
        !!contentScrollEl &&
        contentScrollEl.contains(targetEl)
      ) {
        // Use a small tolerance (e.g., 1 pixel) for scrollTop to account for
        // floating point inaccuracies or slight browser variations.
        isScrolledAtTop = contentScrollEl.scrollTop <= 1;
      }

      const touchData = e?.touches[0];
      // If at the top of the scrollable content and a drag start point hasn't been set yet,
      // record the initial Y position of the touch.
      if (isScrolledAtTop && startDragYPoint === null && !!touchData) {
        startDragYPoint = touchData.clientY;
      }

      // If no valid start point, or no touch data, or not at the top scroll position, abort.
      // This ensures the drawer only reacts to downward pulls from the scroll top.
      if (!startDragYPoint || !touchData || !isScrolledAtTop) return;

      // Calculate the vertical drag distance.
      const downwardDragDistance = touchData.clientY - startDragYPoint;
      if (downwardDragDistance < SCROLL_THRESHOLD) return;

      // --- Initiate Drawer Collapse Animation ---
      isAnimateDrawer = true; // Set flag to prevent re-triggering animation
      const ratio = SNAP_Y_PERCENT[1];
      const offsetHeight =
        drawerRef.current?.offsetHeight || window.innerHeight;

      // Animate the drawer's Y position to the target snap point.
      animate(yPixelOffset, ratio * offsetHeight, {
        type: 'spring',
        power: DRAG_TRANSITION.power,
        stiffness: DRAG_TRANSITION.bounceStiffness,
        damping: DRAG_TRANSITION.bounceDamping,
      }).finished.then(() => {
        // Once the animation completes, reset flags and remove temporary listeners.
        isAnimateDrawer = false;
        removeListeners(); // Remove all listeners, they will be re-added if conditions met
      });

      // Prevent default browser scroll/drag behavior for this event
      // This is crucial to prevent both the drawer and the content from trying to move simultaneously
      e.preventDefault();
    };

    /**
     * Handles the 'touchend' event.
     * Resets the drag start point and removes the touchmove listener.
     */
    const onTouchEnd = () => {
      startDragYPoint = null;
      window.removeEventListener('touchmove', onTouchMove);
    };

    // --- Effect Initialization and Cleanup ---
    // This hook should only be active if the drawer is currently fully expanded.
    // If not fully expanded, remove any existing listeners and do nothing.
    if (!contentEl || !isFullyExpanded) return removeListeners;

    // Add initial touch event listeners when the drawer is fully expanded.
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return removeListeners;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerContentRef, isFullyExpanded]);

  useMotionValueEvent(yPixelOffset, 'change', (val) => {
    const drawerEl = drawerRef.current;
    if (!drawerEl) return;

    const offsetHeight = getOffsetHeight(drawerEl);
    contentHeight.set(offsetHeight - val - contentHeightSpace);

    const percent = (val / offsetHeight) * 100;
    setIsFullyExpanded(percent <= 30);

    if (footer) {
      setShowFooter(percent <= 80);
    }
  });

  const handleBackButton = () => {
    safeCall(onClickBackButton);
  };

  return (
    <aside className="xl:hidden">
      {!!isShowBackButton && !isFullyExpanded && (
        <Button
          className="absolute top-4 left-4 z-10 shadow-sm xl:hidden"
          size="sm"
          color="white"
          aria-label="Mobile Back to Search"
          iconBefore={<IconArrowBack />}
          {...createDataAttributes({
            test: 'mobile-back-button',
          })}
          onClick={handleBackButton}
        />
      )}

      <motion.div
        ref={drawerRef}
        {...dragProps}
        className={cn(
          'absolute inset-0 z-10 rounded-t-sm bg-white shadow-lg',
          'after:center-x after:absolute after:top-2 after:h-1 after:w-16 after:cursor-row-resize after:rounded-full after:bg-gray-300'
        )}
        {...createDataAttributes({
          test: 'mobile-drawer-content',
        })}
      >
        {!isFullyExpanded && (
          <MapControlsMobile onClickCurrentLocation={onClickCurrentLocation} />
        )}
        <div ref={drawerContentRef} className="absolute inset-0 top-5">
          {!!header && header}
          <motion.div
            ref={drawerContentScrollRef}
            style={{ height: contentHeight }}
            className={cn({
              // Enable scroll only when it's fully expanded
              'overflow-y-auto overscroll-none': isFullyExpanded,
              // Lock scroll when it's not fully expanded
              'overflow-hidden': !isFullyExpanded,
            })}
          >
            {children}
          </motion.div>
        </div>
      </motion.div>

      {!!footer && (
        <motion.div
          variants={FOOTER_ANIMATE_VARIANTS}
          initial="slideOut"
          animate={showFooter ? 'slideIn' : 'slideOut'}
          transition={{ duration: 0.4, ease: [0.25, 0.74, 0.22, 0.99] }}
          className={cn(
            'fixed bottom-0 left-0 z-10 flex w-full items-center justify-center bg-white pt-3 pb-6',
            ContainerStyled({ size: 'mobile-only' })
          )}
          {...createDataAttributes({
            test: 'mobile-drawer-footer',
          })}
        >
          {footer}
        </motion.div>
      )}
    </aside>
  );
};
